/**
 * Web-to-app seamless handoff — data-layer client (feature #182).
 *
 * Thin typed wrapper over `apiClient` for the endpoints backing the
 * web-to-app configuration + monitoring page:
 *   - GET    /api/v1/web-to-app/configs                list tenant configs
 *   - POST   /api/v1/web-to-app/configs                create a config
 *   - PATCH  /api/v1/web-to-app/configs/:id            partial update
 *   - DELETE /api/v1/web-to-app/configs/:id            soft-delete
 *   - GET    /api/v1/web-to-app/outcomes               list outcomes + funnel
 *   - GET    /api/v1/web-to-app/handoff/:campaign      public interstitial
 *                                                      (used only for the
 *                                                      "preview" link)
 *
 * Mirrors the server-side view shape in
 * apps/api/src/routes/web-to-app.ts. Keep this file dependency-free beyond
 * `apiClient` + `apiBaseUrl` so the pure helpers stay trivially testable.
 */

import { apiBaseUrl, apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Types — mirrors the backend view shape.
// ---------------------------------------------------------------------------

export type HandoffOutcome =
	| "deep_link_attempted"
	| "app_store_redirect"
	| "web_landing";

export const HANDOFF_OUTCOMES: readonly HandoffOutcome[] = [
	"deep_link_attempted",
	"app_store_redirect",
	"web_landing",
] as const;

export function isHandoffOutcome(v: unknown): v is HandoffOutcome {
	return (
		v === "deep_link_attempted" ||
		v === "app_store_redirect" ||
		v === "web_landing"
	);
}

export interface WebToAppConfigRow {
	id: string;
	campaign_id: string;
	ios_app_id: string;
	android_package: string;
	web_fallback_url: string;
	ios_universal_link: string;
	android_app_link: string;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface WebToAppListResponse {
	rows: WebToAppConfigRow[];
}

export interface WebToAppOutcomeRow {
	id: string;
	campaign_id: string;
	click_id: string;
	outcome: HandoffOutcome;
	created_at: number;
}

export interface OutcomeFunnel {
	deep_link_attempted: number;
	app_store_redirect: number;
	web_landing: number;
	total: number;
}

export interface WebToAppOutcomesResponse {
	rows: WebToAppOutcomeRow[];
	funnel: OutcomeFunnel;
}

// ---------------------------------------------------------------------------
// Validators — the server enforces the same bounds; mirror them here so we
// can surface feedback before the round trip.
// ---------------------------------------------------------------------------

const CAMPAIGN_ID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;
const IOS_APP_ID_RE = /^[0-9]{4,12}$/;
const ANDROID_PACKAGE_RE =
	/^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)+$/;

export function isValidCampaignId(v: unknown): v is string {
	return typeof v === "string" && CAMPAIGN_ID_RE.test(v);
}

export function isValidIosAppId(v: unknown): boolean {
	if (v === "" || v === undefined || v === null) return true;
	if (typeof v !== "string") return false;
	const trimmed = v.trim();
	if (trimmed === "") return true;
	if (IOS_APP_ID_RE.test(trimmed)) return true;
	return isValidHttpUrl(trimmed);
}

export function isValidAndroidPackage(v: unknown): boolean {
	if (v === "" || v === undefined || v === null) return true;
	if (typeof v !== "string") return false;
	const trimmed = v.trim();
	if (trimmed === "") return true;
	if (ANDROID_PACKAGE_RE.test(trimmed) && trimmed.length <= 255) return true;
	return isValidHttpUrl(trimmed);
}

export function isValidHttpUrl(v: unknown): v is string {
	if (typeof v !== "string") return false;
	const trimmed = v.trim();
	if (trimmed.length === 0 || trimmed.length > 2048) return false;
	try {
		const u = new URL(trimmed);
		return u.protocol === "http:" || u.protocol === "https:";
	} catch {
		return false;
	}
}

export function isValidOptionalUrl(v: unknown): boolean {
	if (v === "" || v === undefined || v === null) return true;
	return isValidHttpUrl(v);
}

// ---------------------------------------------------------------------------
// Remote bindings.
// ---------------------------------------------------------------------------

const BASE = "/web-to-app";

export interface CreateConfigArgs {
	campaign_id: string;
	ios_app_id?: string;
	android_package?: string;
	web_fallback_url: string;
	ios_universal_link?: string;
	android_app_link?: string;
}

export async function createWebToAppConfig(
	args: CreateConfigArgs,
): Promise<WebToAppConfigRow> {
	const body: Record<string, unknown> = {
		campaign_id: args.campaign_id,
		web_fallback_url: args.web_fallback_url,
	};
	if (args.ios_app_id !== undefined) body.ios_app_id = args.ios_app_id;
	if (args.android_package !== undefined)
		body.android_package = args.android_package;
	if (args.ios_universal_link !== undefined)
		body.ios_universal_link = args.ios_universal_link;
	if (args.android_app_link !== undefined)
		body.android_app_link = args.android_app_link;
	return apiClient.post<WebToAppConfigRow>(`${BASE}/configs`, body);
}

export async function fetchWebToAppConfigs(): Promise<WebToAppListResponse> {
	return apiClient.get<WebToAppListResponse>(`${BASE}/configs`);
}

export interface PatchConfigArgs {
	ios_app_id?: string;
	android_package?: string;
	web_fallback_url?: string;
	ios_universal_link?: string;
	android_app_link?: string;
	active?: boolean;
}

export async function patchWebToAppConfig(
	id: string,
	patch: PatchConfigArgs,
): Promise<WebToAppConfigRow> {
	return apiClient.patch<WebToAppConfigRow>(
		`${BASE}/configs/${encodeURIComponent(id)}`,
		patch as unknown as Record<string, unknown>,
	);
}

export async function deleteWebToAppConfig(
	id: string,
): Promise<{ ok: true; id: string }> {
	return apiClient.delete<{ ok: true; id: string }>(
		`${BASE}/configs/${encodeURIComponent(id)}`,
	);
}

export async function fetchWebToAppOutcomes(
	campaignId?: string,
	limit?: number,
): Promise<WebToAppOutcomesResponse> {
	const query: Record<string, string | number> = {};
	if (campaignId && campaignId.length > 0) query.campaign_id = campaignId;
	if (typeof limit === "number" && limit > 0) query.limit = limit;
	return apiClient.get<WebToAppOutcomesResponse>(`${BASE}/outcomes`, {
		query,
	});
}

/**
 * Build the public handoff preview URL for a campaign. The page uses this
 * to drop an "Open interstitial" link operators can click from a desktop to
 * eyeball the markup.
 */
export function buildHandoffUrl(campaignId: string): string {
	const safe = encodeURIComponent(campaignId);
	return `${apiBaseUrl}${BASE}/handoff/${safe}`;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export interface WebToAppSummary {
	total: number;
	active: number;
	inactive: number;
	with_ios: number;
	with_android: number;
}

export function computeConfigSummary(
	rows: readonly WebToAppConfigRow[],
): WebToAppSummary {
	let active = 0;
	let inactive = 0;
	let withIos = 0;
	let withAndroid = 0;
	for (const r of rows) {
		if (r.active) active += 1;
		else inactive += 1;
		if (
			r.ios_universal_link.length > 0 ||
			r.ios_app_id.length > 0
		) {
			withIos += 1;
		}
		if (
			r.android_app_link.length > 0 ||
			r.android_package.length > 0
		) {
			withAndroid += 1;
		}
	}
	return {
		total: rows.length,
		active,
		inactive,
		with_ios: withIos,
		with_android: withAndroid,
	};
}

export function sortConfigRows(
	rows: readonly WebToAppConfigRow[],
): WebToAppConfigRow[] {
	return [...rows].sort((a, b) => {
		if (a.campaign_id !== b.campaign_id) {
			return a.campaign_id < b.campaign_id ? -1 : 1;
		}
		return b.created_at - a.created_at;
	});
}

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatTimestamp(unixSec: number | null | undefined): string {
	if (
		unixSec === null ||
		unixSec === undefined ||
		!Number.isFinite(unixSec) ||
		unixSec <= 0
	) {
		return "Never";
	}
	const d = new Date(unixSec * 1000);
	return d.toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatOutcomeLabel(outcome: HandoffOutcome): string {
	if (outcome === "deep_link_attempted") return "Deep link";
	if (outcome === "app_store_redirect") return "App store";
	return "Web landing";
}

/** Compute the share of each outcome in a funnel, percent 0..100. */
export function computeFunnelShares(funnel: OutcomeFunnel): {
	deep_link_attempted: number;
	app_store_redirect: number;
	web_landing: number;
} {
	if (funnel.total <= 0) {
		return {
			deep_link_attempted: 0,
			app_store_redirect: 0,
			web_landing: 0,
		};
	}
	const pct = (n: number) =>
		Math.round((n / funnel.total) * 10000) / 100;
	return {
		deep_link_attempted: pct(funnel.deep_link_attempted),
		app_store_redirect: pct(funnel.app_store_redirect),
		web_landing: pct(funnel.web_landing),
	};
}

export const webToAppQueryKeys = {
	all: ["web-to-app"] as const,
	configs: () => [...webToAppQueryKeys.all, "configs"] as const,
	outcomes: (campaignId?: string) =>
		campaignId
			? ([...webToAppQueryKeys.all, "outcomes", campaignId] as const)
			: ([...webToAppQueryKeys.all, "outcomes"] as const),
};
